import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { ActionButton, H5, Table } from "components";
import { AddCircle } from "assets/logos/icons/MediumIcons";
import { getPropertiesData } from "./helpers";
import RulesTableAddProperty from "./RulesTableAddProperty.component";
import ValidateButton from "components/Buttons/ValidateButton";
import {
  addNewPropertyPvaToTableAction,
  addNewPropertyToTableAction,
  addPropertyRowToTable,
  deleteTableRow,
  editTableAction,
  reorderTableAction,
} from "store/reducers/products/tables/tables.actions";
import { RulesTableContext } from "../../pages/ProductDetail/ProductLineRulesTables/RulesTableContext";
import { ObjectAssignedEnum } from "types/validation";
import { AddMultipleRowsButton } from "./AddMultipleRowsButton.component";
import ExportRuleTableToTSVButton from "./ExportRuleTableToTSVButton";
import BlanketPriceChangeButton from "./BlanketPriceChangeButton";
import DeleteRuleTableButton from "./DeleteRuleTableButton";
import RulesTableHeader from "./RulesTableHeader";
import colors from "theme/colors";

/** Adapter so ActionButton can render the table's existing AddCircle SVG. */
function AddRowIcon() {
  return <AddCircle />;
}

export type RulesTableVariant = "standalone" | "embedded" | "preview";

export const RULE_TABLE_ROW_RENDER_LIMIT = 50;

export default function RulesTable({
  id,
  ruleTableData,
  hideCommentsAndSources = false,
  variant = "standalone",
}: {
  id: string;
  ruleTableData: any;
  hideCommentsAndSources?: boolean;
  /** Marks nested usage (e.g. Table Summary detail). Hide chrome only when requested later.
   *  `preview` is a read-only snapshot (import replace candidate). */
  variant?: RulesTableVariant;
}) {
  const isEmbedded = variant === "embedded";
  const isPreview = variant === "preview";
  const [newPropertyId, setNewPropertyId] = useState("");
  const [newPropertyPvaId, setNewPropertyPvaId] = useState("");
  const [showAllRows, setShowAllRows] = useState(false);

  useEffect(() => {
    setNewPropertyPvaId("");
  }, [ruleTableData.tableType]);

  const totalRows = ruleTableData?.rows?.length || 0;
  const isRowLimitHit = totalRows > RULE_TABLE_ROW_RENDER_LIMIT;
  const isRenderingAllRows = showAllRows || !isRowLimitHit;
  const visibleRowsCount = isRenderingAllRows
    ? undefined
    : RULE_TABLE_ROW_RENDER_LIMIT;
  const propertiesData = useMemo(
    () => getPropertiesData(ruleTableData, visibleRowsCount, isPreview),
    [isPreview, ruleTableData, visibleRowsCount]
  );
  const dispatch = useDispatch();

  const canAddRows =
    !isPreview && !!ruleTableData.properties?.length && isRenderingAllRows;

  const PropertiesOptions = {
    // Add-row controls live in AddRowsRow below the table.
    canAddRow: false,
    show: true,
    rowDeleteAction: isPreview
      ? undefined
      : (row: any) => dispatch(deleteTableRow(ruleTableData, row[0].rowId)),
  };

  const addNewProperty = () => {
    //If we have no property selected, abort!
    if (!newPropertyId) return;

    if (newPropertyPvaId) {
      dispatch(
        addNewPropertyPvaToTableAction(id, newPropertyId, newPropertyPvaId)
      );
      return;
    }
    dispatch(addNewPropertyToTableAction(id, [newPropertyId]));
  };

  const reOrderProperties = (items: any) => {
    const newItems = items
      .map((item: any) => item.id)
      .filter((item) => item !== "NewColProps");

    dispatch(
      editTableAction({
        tableId: id,
        updateData: { propertyIds: newItems },
      })
    );
  };

  const reOrderRows = (
    items: any,
    target,
    destinationPosition,
    sourcePosition
  ) => {
    dispatch(
      reorderTableAction({
        tableId: id,
        targetPropertyId: target,
        destinationPosition,
        sourcePosition,
      })
    );
  };

  return (
    <Wrapper $embedded={isEmbedded || isPreview}>
      <RulesTableContext.Provider key={id} value={ruleTableData}>
        {!isPreview && (
          <HeaderWrapper>
            <H5 bold color={colors.muttedHeadlines}>
              Actions:
            </H5>

            <ValidateButton
              objectType={ObjectAssignedEnum.RULETABLE}
              objectId={id}
            />
            <ExportRuleTableToTSVButton ruleTableId={id} />
            <BlanketPriceChangeButton ruleTableData={ruleTableData} />
            <DeleteRuleTableButton
              tableId={id}
              tableType={ruleTableData.tableType}
            />
          </HeaderWrapper>
        )}

        <RulesTableHeader
          ruleTableData={ruleTableData}
          hideCommentsAndSources={hideCommentsAndSources}
          readOnly={isPreview}
        />
        <BottomBox>
          <TableWrapper>
            <StyledTable
              data={propertiesData}
              {...PropertiesOptions}
              size={"small"}
              tableWidth={`${propertiesData?.header?.length * 3}px`}
              fixHeaderWidth={true}
              onDragEndActionHeader={isPreview ? undefined : reOrderProperties}
              onDragEndActionRows={
                isPreview || !isRenderingAllRows ? undefined : reOrderRows
              }
            />
            {isRowLimitHit && !showAllRows && (
              <ShowMoreButton onClick={() => setShowAllRows(true)}>
                Show more ({totalRows} total rows)
              </ShowMoreButton>
            )}
            {canAddRows && (
              <AddRowsRow>
                <AddRowButtonWrap>
                  <ActionButton
                    variant="outlined"
                    size="small"
                    color="primary"
                    icon={AddRowIcon}
                    ariaLabel="Add row"
                    fullWidth
                    onClick={() =>
                      dispatch(addPropertyRowToTable(ruleTableData))
                    }
                  >
                    Add row
                  </ActionButton>
                </AddRowButtonWrap>
                <AddMultipleRowsButton ruletableData={ruleTableData} />
              </AddRowsRow>
            )}
          </TableWrapper>
          {!isPreview && (
            <RulesTableAddProperty
              newPropertyId={newPropertyId}
              setNewPropertyId={setNewPropertyId}
              newPropertyPvaId={newPropertyPvaId}
              setNewPropertyPvaId={setNewPropertyPvaId}
              addNewProperty={addNewProperty}
              ruleTableType={ruleTableData.tableType}
            />
          )}
        </BottomBox>
      </RulesTableContext.Provider>
    </Wrapper>
  );
}

const Wrapper = styled.div<{ $embedded?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.$embedded
      ? `
    padding: 8px 12px 12px;
  `
      : ""}
`;

const HeaderWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;

  & > * {
    margin-top: 0px;
    margin-bottom: 0px;
  }
`;

const StyledTable = styled(Table)`
  > table {
    border: none;
  }
  > table > tbody > tr > td {
    min-width: 180px !important;
    max-width: 180px;
  }
`;

const BottomBox = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 8px;
  padding-bottom: 16px;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const AddRowsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

/** Short fixed-width Add row control (replaces the full-width Table add-row button). */
const AddRowButtonWrap = styled.div`
  width: 110px;
  min-width: 110px;
  flex-shrink: 0;
`;

const ShowMoreButton = styled.button`
  margin-top: 8px;
  padding: 8px 12px;
  border: 1px solid ${(props) => props.theme.colors.secondary};
  background-color: ${(props) => props.theme.colors.background.primaryLabels};
  color: ${(props) => props.theme.colors.secondary};
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  pointer-events: auto;

  :hover {
    opacity: 0.7;
  }
`;
